import i18n from "i18next";
// Core entry only: importing "zod" here would pull the whole schema library
// (~25KB gzip) into the entry chunk just to set the global config.
import { config as zodConfig, type $ZodErrorMap } from "zod/v4/core";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";

// Bundle ONLY the fallback language (en) so first paint is never blocked and
// fallbackLng keys are always available synchronously. The other 20 locales are
// lazy-loaded as separate Vite chunks, fetched only for the active language —
// this keeps ~700 KB of translations out of the initial bundle.
import enCommon from "../locales/en/common.json";

const STORAGE_KEY = "i18nextLng";

const SUPPORTED_LNGS = [
  "en",
  "th",
  "zh",
  "zh-TW",
  "es",
  "ar",
  "fr",
  "de",
  "it",
  "ja",
  "ko",
  "nl",
  "pl",
  "pt",
  "ru",
  "sv",
  "tr",
  "hi",
  "id",
  "vi",
  "ms",
];

// Each non-bundled locale file becomes its own code-split chunk. import.meta.glob
// is statically analyzable, so Rollup/rolldown emits one chunk per language. en is
// excluded because it's statically imported above and bundled into the main chunk.
const localeModules = import.meta.glob([
  "../locales/*/common.json",
  "!../locales/en/common.json",
]);

i18n
  .use(LanguageDetector)
  // Lazy-load translations on demand. Unknown/region codes (e.g. "de-DE") resolve
  // empty so i18next falls through to the base language ("de") and then to "en".
  .use(
    resourcesToBackend((language: string, namespace: string) => {
      const loader = localeModules[`../locales/${language}/${namespace}.json`];
      if (!loader) return Promise.resolve({});
      return (loader() as Promise<{ default: Record<string, unknown> }>).then(
        (mod) => mod.default,
      );
    }),
  )
  .use(initReactI18next)
  .init({
    // Language settings (lng omitted to enable automatic detection)
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LNGS,

    // Namespace settings
    defaultNS: "common",
    ns: ["common"],

    // Pre-bundle en so it's available synchronously at boot...
    resources: {
      en: {
        common: enCommon,
      },
    },
    // ...and keep the backend active to lazy-load the other languages.
    partialBundledLanguages: true,

    // Language detection options
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: STORAGE_KEY,
    },

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Development settings
    debug: import.meta.env.DEV,

    // React settings
    react: {
      useSuspense: false,
    },
  });

// Zod ships translated validation messages per locale. Statically enumerated
// dynamic imports so each locale is its own lazy chunk (same reasoning as
// localeModules above); English is Zod's built-in default and needs no load.
// Schema-level custom messages always win over the locale defaults.
const zodLocales: Record<
  string,
  () => Promise<{ default: () => { localeError: $ZodErrorMap } }>
> = {
  th: () => import("zod/v4/locales/th.js"),
  zh: () => import("zod/v4/locales/zh-CN.js"),
  "zh-TW": () => import("zod/v4/locales/zh-TW.js"),
  es: () => import("zod/v4/locales/es.js"),
  ar: () => import("zod/v4/locales/ar.js"),
  fr: () => import("zod/v4/locales/fr.js"),
  de: () => import("zod/v4/locales/de.js"),
  it: () => import("zod/v4/locales/it.js"),
  ja: () => import("zod/v4/locales/ja.js"),
  ko: () => import("zod/v4/locales/ko.js"),
  nl: () => import("zod/v4/locales/nl.js"),
  pl: () => import("zod/v4/locales/pl.js"),
  pt: () => import("zod/v4/locales/pt.js"),
  ru: () => import("zod/v4/locales/ru.js"),
  sv: () => import("zod/v4/locales/sv.js"),
  tr: () => import("zod/v4/locales/tr.js"),
  hi: () => import("zod/v4/locales/hi.js"),
  id: () => import("zod/v4/locales/id.js"),
  vi: () => import("zod/v4/locales/vi.js"),
  ms: () => import("zod/v4/locales/ms.js"),
};

function applyZodLocale(lng: string) {
  const loader = zodLocales[lng];
  if (!loader) {
    // en (Zod's default) or an unknown language — reset to built-in English so
    // a previous non-English locale doesn't stick after switching back.
    zodConfig({ localeError: undefined });
    return;
  }
  loader()
    .then((mod) => zodConfig(mod.default()))
    // A failed locale chunk load just leaves English messages — never fatal.
    .catch(() => {});
}

// Update <html lang> and Content-Language meta when language changes
// This prevents browser translation prompts for users viewing in their native language
// See: https://github.com/facebook/react/issues/11538
i18n.on("languageChanged", (lng) => {
  applyZodLocale(lng);
  document.documentElement.lang = lng;

  // Also update Content-Language meta tag for Edge and other browsers
  const contentLangMeta = document.querySelector(
    'meta[http-equiv="Content-Language"]',
  );
  if (contentLangMeta) {
    contentLangMeta.setAttribute("content", lng);
  }
});

// Set initial language on the html element
if (i18n.language) {
  applyZodLocale(i18n.language);
  document.documentElement.lang = i18n.language;

  const contentLangMeta = document.querySelector(
    'meta[http-equiv="Content-Language"]',
  );
  if (contentLangMeta) {
    contentLangMeta.setAttribute("content", i18n.language);
  }
}

export default i18n;
